import { apiGet, apiPost } from './client';
import type { AIProcessLibrary, OperationResult, WorkflowCatalogEntry } from './types';

// Read-only API for the workflow library (`ai.AIProcessLibrary`). Editing
// and run-trigger endpoints land in a follow-up — workflows can fan out to
// AI calls, PDF generation, care-team notifications, and SQL writes, so the
// "Run" surface needs careful scoping/safety review before it's exposed.

export interface ProcessLibrarySearchParams {
  merchantId?: number | null;
  search?: string;
  includeGlobal?: boolean;
}

export async function searchProcessLibrary(
  params: ProcessLibrarySearchParams = {}
): Promise<AIProcessLibrary[]> {
  const { merchantId, search = '', includeGlobal = true } = params;
  const wrapped = await apiGet<OperationResult<AIProcessLibrary[]>>(
    '/api/AIProcessLibrary/Search',
    { merchantId, search, includeGlobal }
  );
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || 'Failed to load workflow library');
  }
  return wrapped.data ?? [];
}

export async function getProcessLibraryById(id: number): Promise<AIProcessLibrary> {
  const wrapped = await apiGet<OperationResult<AIProcessLibrary>>(
    `/api/AIProcessLibrary/GetById/${id}`
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || `Workflow ${id} not found`);
  }
  return wrapped.data;
}

// POST /api/AIProcessLibrary/Update — full-model update (matches the
// AssistantAIController.Update convention). Server stamps UpdatedBy from
// the calling employee; we send the entity exactly as we loaded it with
// edits applied, so any unedited fields ride along untouched.
export async function updateProcessLibrary(model: AIProcessLibrary): Promise<AIProcessLibrary> {
  const wrapped = await apiPost<OperationResult<AIProcessLibrary>>(
    '/api/AIProcessLibrary/Update',
    model
  );
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || 'Failed to update workflow');
  }
  return wrapped.data ?? model;
}

// GET /api/AIProcessLibrary/Catalog — every workflow that can run for the selected merchant, AS IT RUNS:
// database rows and code-only workflows, with their effective steps, source and agents. The list page reads
// this instead of the raw rows (a row with an empty workflow used to read "No steps defined").
export async function getWorkflowCatalog(merchantId: number | null, search = ''): Promise<WorkflowCatalogEntry[]> {
  const wrapped = await apiGet<OperationResult<WorkflowCatalogEntry[]>>('/api/AIProcessLibrary/Catalog', { merchantId, search });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load workflows');
  return wrapped.data ?? [];
}

// GET /api/AIProcessLibrary/Effective?name= — one workflow as it runs (detail page summary + pipeline).
export async function getEffectiveWorkflow(name: string, merchantId: number | null): Promise<WorkflowCatalogEntry> {
  const wrapped = await apiGet<OperationResult<WorkflowCatalogEntry>>('/api/AIProcessLibrary/Effective', { name, merchantId });
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || `Workflow ${name} not found`);
  return wrapped.data;
}
